import {
  IPlusOrMinus,
  IPrice,
  IQuantityPrice,
  IRangePrice,
  IUnitPrice,
  IVatMessage,
  ZeroDisplayType as LegacyZeroDisplayType,
} from "../types/pricing";

import { ICombinedPriceConfig, IDifferentialPriceConfig, IDifferentialPriceForQuantityConfig, IQuantityForPriceConfig, IRangePriceConfig, PriceDisplayMode, ZeroDisplayType } from "@vp/ep-pricing";
import {
  Price,
  PricePresentationType,
  PriceProperties,
  PricesTypesBasedOnPricePresentation,
} from "../types/prices";
import { isEmptyPrice } from "./price-calculator-helper";

const getBasePricePropsLegacy = (priceProps: PriceProperties) => ({
  minimumFractionDigits: priceProps.minimumFractionDigits,
  culture: priceProps.locale,
  currency: priceProps.currency,
  vatInc: priceProps.vatInc,
});

const getBasePriceProps = (priceProps: PriceProperties) => ({
  minimumFractionDigits: priceProps.minimumFractionDigits,
  locale: priceProps.locale,
  currency: priceProps.currency,
  vatInc: priceProps.vatInc,
});

export const getPricePerUnit = (
  prices: Record<PricesTypesBasedOnPricePresentation<PricePresentationType.PricePerUnit>, Price>,
  priceProperties: PriceProperties,
): IUnitPrice => {
  const taxInclusivity = priceProperties.vatInc ? "taxed" : "untaxed";

  return {
    ...getBasePricePropsLegacy(priceProperties),
    unitListPrice: prices.unitListPrice[taxInclusivity],
    unitDiscountPrice: prices.unitDiscountedPrice[taxInclusivity],
  };
};

export const getQuantityPrice = (
  prices: Record<
    PricesTypesBasedOnPricePresentation<PricePresentationType.QtyStartingAtPrice>,
    Price
  >,
  priceProperties: PriceProperties & { quantity: number },
): IQuantityPrice => {
  const taxInclusivity = priceProperties.vatInc ? "taxed" : "untaxed";

  return {
     ...getBasePricePropsLegacy(priceProperties),
    quantity: priceProperties.quantity,
    listPrice: prices.totalListPrice[taxInclusivity],
    discountPrice: prices.totalDiscountedPrice[taxInclusivity],
  };
};

export const getQuantityFromPrice = (
  prices: Record<PricesTypesBasedOnPricePresentation<PricePresentationType.QtyFromPrice>, Price>,
  priceProperties: PriceProperties & { quantity: number },
): IQuantityPrice => {
  const taxInclusivity = priceProperties.vatInc ? "taxed" : "untaxed";
  return {
     ...getBasePricePropsLegacy(priceProperties),
    quantity: priceProperties.quantity,
    listPrice: prices.totalListPrice[taxInclusivity],
    discountPrice: prices.totalDiscountedPrice[taxInclusivity],
  };
};

export const getRangedPrice = (
  prices: Record<PricesTypesBasedOnPricePresentation<PricePresentationType.RangePrice>, Price>,
  priceProperties: PriceProperties,
): IRangePrice => {
  const taxInclusivity = priceProperties.vatInc ? "taxed" : "untaxed";

  return {
     ...getBasePricePropsLegacy(priceProperties),
    lowestListPrice: prices.lowestListPrice[taxInclusivity],
    highestListPrice: prices.highestListPrice[taxInclusivity],
    lowestDiscountPrice: prices.lowestDiscountPrice[taxInclusivity],
    highestDiscountPrice: prices.highestDiscountPrice[taxInclusivity],
  };
};

export const getUnitRangedPrice = (
  prices: Record<PricesTypesBasedOnPricePresentation<PricePresentationType.UnitRangePrice>, Price>,
  priceProperties: PriceProperties,
): IRangePrice => {
  const taxInclusivity = priceProperties.vatInc ? "taxed" : "untaxed";

  return {
     ...getBasePricePropsLegacy(priceProperties),
    lowestListPrice: prices.lowestListPrice[taxInclusivity],
    highestListPrice: prices.highestListPrice[taxInclusivity],
    lowestDiscountPrice: prices.lowestDiscountPrice[taxInclusivity],
    highestDiscountPrice: prices.highestDiscountPrice[taxInclusivity],
  };
};

export const getPriceForQuantityUnits = (
  prices: Record<
    PricesTypesBasedOnPricePresentation<PricePresentationType.PriceForQtyUnits>,
    Price
  >,
  priceProperties: PriceProperties & { quantity: number },
): IQuantityPrice => {
  const taxInclusivity = priceProperties.vatInc ? "taxed" : "untaxed";
  return {
     ...getBasePricePropsLegacy(priceProperties),
    quantity: priceProperties.quantity,
    listPrice: prices.totalListPrice?.[taxInclusivity] ?? 0,
    discountPrice: prices.totalDiscountedPrice?.[taxInclusivity] ?? 0,
  };
};

export const getPriceWithQtyAndUnit = (
  prices: Record<
    PricesTypesBasedOnPricePresentation<PricePresentationType.PriceWithQtyAndUnit>,
    Price
  > & {
    unitListPriceForMoq?: Price,
  },
  priceProperties: PriceProperties & { quantity: number },
): IQuantityPrice & IUnitPrice => {
  const taxInclusivity = priceProperties.vatInc ? "taxed" : "untaxed";
  return {
     ...getBasePricePropsLegacy(priceProperties),
    quantity: priceProperties.quantity,
    listPrice: prices.totalListPrice?.[taxInclusivity] ?? 0,
    discountPrice: prices.totalDiscountedPrice?.[taxInclusivity] ?? 0,
    unitListPrice: prices.unitListPriceForMoq?.[taxInclusivity] ?? prices.unitListPrice?.[taxInclusivity],
    unitDiscountPrice: prices.unitDiscountedPrice?.[taxInclusivity],
  };
};

export const getPrice = (
  prices: Record<PricesTypesBasedOnPricePresentation<PricePresentationType.RawPrice>, Price>,
  priceProperties: PriceProperties,
): IPrice => {
  const taxInclusivity = priceProperties.vatInc ? "taxed" : "untaxed";

  return {
     ...getBasePricePropsLegacy(priceProperties),
    listPrice: prices.totalListPrice[taxInclusivity],
    discountPrice: prices.totalDiscountedPrice[taxInclusivity],
  };
};

export const getRawAndUnitPrice = (
  prices: Record<
    PricesTypesBasedOnPricePresentation<PricePresentationType.RawAndPricePerUnit>,
    Price
  >,
  priceProperties: PriceProperties,
): IPrice & IUnitPrice => {
  const taxInclusivity = priceProperties.vatInc ? "taxed" : "untaxed";

  return {
     ...getBasePricePropsLegacy(priceProperties),
    listPrice: prices.totalListPrice[taxInclusivity],
    discountPrice: prices.totalDiscountedPrice[taxInclusivity],
    unitListPrice: prices.unitListPrice[taxInclusivity],
    unitDiscountPrice: prices.unitDiscountedPrice[taxInclusivity],
  };
};

export const getAsLowAsPerPiecePrice = (
  prices: Record<
    PricesTypesBasedOnPricePresentation<PricePresentationType.AsLowAsPricePerPiece>,
    Price
  >,
  priceProperties: PriceProperties,
): IUnitPrice => {
  const taxInclusivity = priceProperties.vatInc ? "taxed" : "untaxed";

  return {
     ...getBasePricePropsLegacy(priceProperties),
    unitListPrice: prices.unitListPrice[taxInclusivity],
    unitDiscountPrice: prices.unitDiscountedPrice[taxInclusivity],
  };
};

export const getPlusMinusPrice = (
  prices: Record<PricesTypesBasedOnPricePresentation<PricePresentationType.PlusOrMinus>, number>,
  priceProperties: PriceProperties,
): IPlusOrMinus => {
  return {
    priceDifference: priceProperties.isPriceNegative
      ? -Math.abs(prices.priceDifference)
      : prices.priceDifference,
    priceDifferenceDiscounted: priceProperties.isPriceNegative
      ? -Math.abs(prices.priceDifferenceDiscounted)
      : prices.priceDifferenceDiscounted,
      minimumFractionDigits: priceProperties.minimumFractionDigits,
    culture: priceProperties.locale,
    currency: priceProperties.currency,
    zeroDisplayType: priceProperties.zeroDisplayType as LegacyZeroDisplayType,
    comparativeDisplayType: priceProperties.comparativeDisplayType,
  };
};

export const getDetailedPriceConfig = (
  prices: Record<PricesTypesBasedOnPricePresentation<PricePresentationType.PriceInfo>, Price> & {
    unitListPrice?: Price,
    unitDiscountedPrice?: Price,
    setupFeeListPrice?: Price,
    setupFeeDiscountedPrice?: Price,
    unitListPriceForMoq?: Price,
    listPriceForMoq?: Price,
    baseListPrice?: Price,
    baseDiscountedPrice?: Price,
  },
  priceProperties: PriceProperties & { quantity: number },
  priceDisplayMode: PriceDisplayMode,
): ICombinedPriceConfig => {
  const showSetUpFeeInfo = !priceProperties.hideSetupFee;
  const showVolumeDiscount = !priceProperties.hideVolumeDiscount;
  const showPerUnitPrice = !priceProperties.hidePerUnitPrice;
  return {
    ...getBasePriceProps(priceProperties),
    listPrice: prices.totalListPrice,
    discountedListPrice: prices.totalDiscountedPrice,
    unitListPrice: showPerUnitPrice ? prices.unitListPrice : undefined,
    unitDiscountedPrice: showPerUnitPrice ? prices.unitDiscountedPrice : undefined,
    setupFee: !showSetUpFeeInfo || isEmptyPrice(prices.setupFeeListPrice) ? undefined : prices.setupFeeListPrice,
    discountedSetupFee: !showSetUpFeeInfo || isEmptyPrice(prices.setupFeeDiscountedPrice) ? undefined : prices.setupFeeDiscountedPrice,
    unitPriceForMoq: showVolumeDiscount ? prices.unitListPriceForMoq : undefined,
    listPriceOfMoq: showVolumeDiscount ? prices.listPriceForMoq : undefined,
    basePrice: prices.baseListPrice,
    discountedBasePrice: prices.baseDiscountedPrice,
    quantity: showPerUnitPrice ? priceProperties.quantity : undefined,
    priceDisplayMode,
    showPriceSummary: showSetUpFeeInfo,
    showSetupFeeAndVat: showSetUpFeeInfo,
    moq: showVolumeDiscount ? priceProperties.moq : undefined,
  };
};

export const getCombinedPriceConfig = (
  prices: Record<PricesTypesBasedOnPricePresentation<PricePresentationType.PriceInfo>, Price> & {
    unitListPrice?: Price,
    unitDiscountedPrice?: Price,
    setupFeeListPrice?: Price,
    setupFeeDiscountedPrice?: Price,
    unitListPriceForMoq?: Price,
    baseListPrice?: Price,
    baseDiscountedPrice?: Price,
  },
  priceProperties: PriceProperties & { quantity: number },
  priceDisplayMode: PriceDisplayMode,
): ICombinedPriceConfig => {
  return {
    ...getBasePriceProps(priceProperties),
    listPrice: prices.totalListPrice,
    discountedListPrice: prices.totalDiscountedPrice,
    unitListPrice: prices.unitListPrice,
    unitDiscountedPrice: prices.unitDiscountedPrice,
    quantity: priceProperties.quantity,
    priceDisplayMode,
  };
};

export const getQuantityForPrice = (
  prices: Record<PricesTypesBasedOnPricePresentation<PricePresentationType.QtyForPrice>, Price>,
  priceProperties: PriceProperties & { quantity: number },
): IQuantityForPriceConfig => {
  return {
    ...getBasePriceProps(priceProperties),
    listPrice: prices.totalListPrice,
    discountedListPrice: prices.totalDiscountedPrice,
    quantity: priceProperties.quantity,
  };
};

export const getFromPriceConfig = (
  prices: Record<PricesTypesBasedOnPricePresentation<PricePresentationType.FromPrice>, Price> & {
    unitListPrice?: Price,
    unitDiscountedPrice?: Price,
  },
  priceProperties: PriceProperties & { quantity: number },
  priceDisplayMode: PriceDisplayMode,
): ICombinedPriceConfig => {
  return {
    ...getBasePriceProps(priceProperties),
    listPrice: prices.totalListPrice,
    discountedListPrice: prices.totalDiscountedPrice,
    unitListPrice: prices.unitListPrice,
    unitDiscountedPrice: prices.unitDiscountedPrice,
    quantity: priceProperties.quantity,
    priceDisplayMode,
  };
};

export const getRangePriceConfig = (
  prices: Record<PricesTypesBasedOnPricePresentation<PricePresentationType.UnitRangePrice>, Price> & {
    setupFeeListPrice?: Price,
    setupFeeDiscountedPrice?: Price,
  },
  priceProperties: PriceProperties,
  isTile: boolean = false,
): IRangePriceConfig => {
  const showSetUpFeeInfo = !priceProperties.hideSetupFee;
  return {
    ...getBasePriceProps(priceProperties),
    lowestListPrice: prices.lowestListPrice,
    highestListPrice: prices.highestListPrice,
    lowestDiscountedPrice: prices.lowestDiscountPrice,
    highestDiscountedPrice: prices.highestDiscountPrice,
    setupFee: !showSetUpFeeInfo || isEmptyPrice(prices.setupFeeListPrice) ? undefined : prices.setupFeeListPrice,
    discountedSetupFee: !showSetUpFeeInfo || isEmptyPrice(prices.setupFeeDiscountedPrice) ? undefined : prices.setupFeeDiscountedPrice,
    isTile,
    showSetupFeeInfo: showSetUpFeeInfo,
  };
};

export const getDifferentialPriceConfig = (
  prices: Record<PricesTypesBasedOnPricePresentation<PricePresentationType.DifferentialPrice>, Price>,
  priceProperties: PriceProperties,
): IDifferentialPriceConfig => {
  return {
    ...getBasePriceProps(priceProperties),
    priceDifference: prices.priceDifference,
    priceDifferenceDiscounted: prices.priceDifferenceDiscounted,
    showComparativeDisplay: priceProperties.showComparativeDisplay,
    zeroDisplayType: priceProperties.zeroDisplayType as ZeroDisplayType,
  };
};

export const getDifferentialPriceForQuantityConfig = (
  prices: Record<PricesTypesBasedOnPricePresentation<PricePresentationType.DifferentialPriceForQuantity>, Price>,
  priceProperties: PriceProperties & { quantity: number },
): IDifferentialPriceForQuantityConfig => {
  return {
    ...getBasePriceProps(priceProperties),
    priceDifference: prices.priceDifference,
    priceDifferenceDiscounted: prices.priceDifferenceDiscounted,
    quantity: priceProperties.quantity,
  };
};

export const getVatInfo = (
  priceProperties: Pick<PriceProperties, "locale" | "vatInc" | "hideShippingMessageInVatMessage">,
): IVatMessage => {
  return {
    culture: priceProperties.locale,
    vatInc: priceProperties.vatInc,
    hideShippingMessageInVatMessage: priceProperties.hideShippingMessageInVatMessage,
  };
};
